import {
  assignIn,
  eq,
  find,
  get,
  isEmpty,
  isMatch,
  isString,
  kebabCase,
  split,
} from 'lodash';
import { Injectable } from '@angular/core';

import { lazyInject } from '@app/shared/decorators/lazy-inject.decorator';
import { rx } from '@app/shared/rx';
import { IPermissionValue } from '@app/shared/types/portal/permission.type';

import { TokenSessionService } from '../session';
import { XeaDataService } from '../xea-data';
import {
  PermissionsType,
  SupportedAppCodes,
} from '@app/shared/types/enums/permission.enum';

@Injectable()
export class PermissionService {
  @lazyInject(TokenSessionService)
  public tokenSessionService: TokenSessionService;
  @lazyInject(XeaDataService)
  public xeaDataService: XeaDataService;

  public static DefaultPermissionSearchCriteria: Partial<IPermissionValue> = {
    app_code: SupportedAppCodes.XVI,
    type: PermissionsType.action,
  };

  public permissions: IPermissionValue[] = [];

  public constructor() {}

  public static checkPermissionByName(
    permission: IPermissionValue,
    fullSearchCriteria: Partial<IPermissionValue>,
  ): boolean {
    const permissionLevelChar = '.';

    const permissionNameArr = split(permission.name, permissionLevelChar);

    if (
      permission.app_code !== SupportedAppCodes.XVI ||
      permissionNameArr.length < 3
    ) {
      return (
        eq(get(permission, 'app_code'), get(fullSearchCriteria, 'app_code')) &&
        eq(get(permission, 'name'), get(fullSearchCriteria, 'name'))
      );
    }

    permissionNameArr.splice(0, 1);
    permissionNameArr[0] = kebabCase(permissionNameArr[0]);

    return isMatch(
      {
        ...permission,
        name: permissionNameArr.join(permissionLevelChar),
      },
      fullSearchCriteria,
    );
  }

  public static buildSearchCriteria(
    searchCriteria: Partial<IPermissionValue> | string,
    appCode?: SupportedAppCodes,
  ): Partial<IPermissionValue> {
    const partialSearchCriteria = isString(searchCriteria)
      ? { name: searchCriteria }
      : searchCriteria;

    const fullSearchCriteria: Partial<IPermissionValue> = assignIn(
      {},
      PermissionService.DefaultPermissionSearchCriteria,
      appCode ? { app_code: appCode } : {},
      partialSearchCriteria,
    );

    return fullSearchCriteria;
  }

  public static getPermissionValue(
    permissions: IPermissionValue[],
    searchCriteria: string,
  ): boolean {
    const targetPermission: IPermissionValue = find(permissions, {
      name: searchCriteria,
    });

    return !!get(targetPermission, 'value', false);
  }

  public static hasPermissions(
    permissions: IPermissionValue[],
    ...requiredPermissions: string[]
  ): boolean {
    return requiredPermissions.every(
      (name) => !!PermissionService.getPermissionValue(permissions, name),
    );
  }

  public getUserPermissions(): Promise<IPermissionValue[]> {
    if (!isEmpty(this.permissions)) {
      return Promise.resolve(this.permissions);
    }

    return this.xeaDataService
      .getRoleById(this.tokenSessionService.roleId)
      .pipe(
        rx.tap(
          (res) => {
            this.permissions = get(res, 'permissions');
          },
          rx.map(() => this.permissions),
        ),
      )
      .toPromise();
  }

  /**
   * judge is have required permission sync
   * @param requiredPermissions check permissions
   * @returns is have required permission
   */
  public hasPermissionsSync(
    requiredPermissions: string[],
    permissionOR: boolean,
  ): boolean {
    if (permissionOR) {
      return requiredPermissions.some(
        (name) =>
          !!PermissionService.getPermissionValue(this.permissions, name),
      );
    }

    return requiredPermissions.every(
      (name) => !!PermissionService.getPermissionValue(this.permissions, name),
    );
  }
}
